import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, CalendarClock, Loader2, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function DirectLineCard() {
  const [info, setInfo] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ preferred_date: "", slot: "", topic: "", phone: "" });

  const load = () => Promise.all([
    api.get("/support/contact").then((r) => setInfo(r.data)),
    api.get("/support/callbacks").then((r) => setBookings(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const book = async () => {
    setBusy(true);
    try {
      await api.post("/support/callbacks", form);
      toast.success("Call requested — we'll confirm by email");
      setOpen(false); setForm({ preferred_date: "", slot: "", topic: "", phone: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!info) return null;
  const next = bookings.find((b) => b.status === "requested");

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-sm min-w-0" data-testid="direct-line-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center"><Phone className="w-4 h-4" /></div>
        <p className="text-sm font-medium text-muted-foreground">Direct line to your specialist</p>
      </div>
      {info.has_access ? (
        <>
          <a href={`tel:${info.phone.replace(/\s/g, "")}`} className="font-display text-2xl font-extrabold tracking-tight hover:text-primary block" data-testid="direct-line-phone">{info.phone}</a>
          <p className="text-xs text-muted-foreground mt-1">{info.name} · {info.hours} · <a href={`mailto:${info.email}`} className="hover:underline">{info.email}</a></p>
          {next && (
            <p className="text-xs mt-3 flex items-center gap-1.5 text-accent" data-testid="direct-line-next-call">
              <CheckCircle2 className="w-3.5 h-3.5" /> Call requested for {new Date(next.preferred_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}, {next.slot}
            </p>
          )}
          <Button size="sm" variant="outline" className="mt-4" onClick={() => setOpen(true)} data-testid="book-call-button">
            <CalendarClock className="w-4 h-4 mr-1.5" /> Book a call
          </Button>
        </>
      ) : (
        <>
          <p className="font-display text-lg font-bold flex items-center gap-2"><Lock className="w-4 h-4 text-muted-foreground" /> Included with Ongoing Support</p>
          <p className="text-xs text-muted-foreground mt-1">Subscribers get a direct number and can book a call any weekday.</p>
          <Button asChild size="sm" className="mt-4" data-testid="direct-line-upgrade"><Link to="/pricing">See plans — from £80/month</Link></Button>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="book-call-dialog">
          <DialogHeader><DialogTitle>Book a call with our compliance specialist</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Preferred date (Mon–Fri)</Label>
                <Input type="date" className="mt-1.5" min={new Date().toISOString().slice(0, 10)} value={form.preferred_date}
                  onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="book-call-date" /></div>
              <div><Label>Your phone number</Label>
                <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07…" data-testid="book-call-phone" /></div>
            </div>
            <div><Label>Time slot</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5" data-testid="book-call-slots">
                {info.slots.map((s) => (
                  <button key={s} type="button" onClick={() => setForm({ ...form, slot: s })}
                    className={`text-xs rounded-lg border px-2 py-2 transition-colors ${form.slot === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}
                    data-testid={`book-call-slot-${s.slice(0, 2)}`}>{s}</button>
                ))}
              </div>
            </div>
            <div><Label>What would you like to cover?</Label>
              <Textarea rows={3} className="mt-1.5" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="book-call-topic" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={book} disabled={busy || !form.preferred_date || !form.slot || form.topic.length < 5 || form.phone.length < 7} data-testid="book-call-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request call"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
